import { flatMap } from "lodash-es";
import React, { FC, Fragment, useCallback } from "react";
import { useMedia } from "react-use";
import { RoutedTabs, useRouter } from "router";

import { IButtonProps } from "@/components/Button";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Section } from "@/components/Section";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { Flex, IStyleInnerMargin, Tab, TabList } from "@/eworkitComponents";
import { RouteButton } from "@/infrastructure/Route";
import { Actions, IActionsRenderProps } from "@/views/components/Actions";
import { router } from "@/views/router";

import { TTabOption } from ".";
import { ITabOption, ITabOptionGroup } from "./types";

import "./_tab-sections.scss";

interface ITabSectionsProps extends Pick<IButtonProps, "variant">, Partial<IStyleInnerMargin> {
  pathName: string;
  routeParam: string;
  options: TTabOption[];
}

export const TabSections: FC<ITabSectionsProps> = ({
  pathName,
  routeParam,
  options,
  variant = "vertical-tab",
  innerMargin,
}) => {
  const reactRouter = useRouter<{}, Record<string, string>>();
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const flatOptions = flatMap(options, o => (o.type === "group" ? o.options : o));
  const paramValue = reactRouter.searchParams[routeParam];
  const foundOptionIndex = flatOptions.findIndex(o => o.value === paramValue);
  const activeOptionIndex = foundOptionIndex === -1 ? 0 : foundOptionIndex;
  const activeOption = flatOptions[activeOptionIndex];

  const buildEmptyViews = useCallback(() => {
    const result: Record<string, (active: boolean, index: number) => JSX.Element> = {};
    for (const option of flatOptions) {
      result[option.value] = () => <React.Fragment key={option.value} />;
    }
    return result;
  }, [pathName, routeParam, options]);

  const buildButton = useCallback(
    (option: ITabOption, active: boolean) => {
      return (
        <RouteButton
          active={active}
          path={
            router.get(option.pathName || pathName, option.params ?? {}, {
              ...option.searchParams,
              [routeParam]: option.value,
            }).path
          }
          variant={isSmallScreen ? "vertical-icon-tab" : variant}
          icon={option.icon}
          iconColor={active ? option.iconColorActive : option.iconColor}
          onClick={option.onClick}>
          {option.label && (
            <Text size="md" wordBreak="all">
              {option.label}
            </Text>
          )}
          {option.renderContent?.(active)}
        </RouteButton>
      );
    },
    [pathName, routeParam],
  );

  const buildViews = useCallback(
    (group: ITabOptionGroup) => {
      const result = buildEmptyViews();

      for (const option of group.options) {
        result[option.value] = (active: boolean, index: number) => <Tab key={index}>{buildButton(option, active)}</Tab>;
      }

      return result;
    },
    [pathName, routeParam, options, buildButton],
  );

  const renderTarget = useCallback(
    (actionsProps: IActionsRenderProps) => {
      return (
        <div>
          {buildButton({ ...activeOption, iconColor: "default" }, false)}
          <Icon
            className={`${baseClass}__dropdown-icon`}
            icon={actionsProps.open ? "dropup" : "dropdown"}
            color="grey2"
            size="lg"
          />
        </div>
      );
    },
    [buildButton, activeOption],
  );

  const baseClass = "tab-sections";

  return (
    <div className={baseClass}>
      {isSmallScreen ? (
        <Actions
          className={`${baseClass}__mobile-actions`}
          placement="bottom"
          renderActiveBar={false}
          renderTarget={renderTarget}
          usePortal>
          <Flex
            className={`${baseClass}__mobile-actions-content`}
            direction="column"
            padding={["sm"]}
            innerBorder
            innerPadding>
            {options.map(
              (o, i) =>
                o.type === "group" &&
                !o.hide && (
                  <TabList key={i} size="lg" direction="column" theme="clean" innerMargin={innerMargin}>
                    <RoutedTabs routeParam={routeParam} views={buildViews(o)} />
                  </TabList>
                ),
            )}
          </Flex>
        </Actions>
      ) : (
        <Flex direction="column" innerMargin="md">
          {options.map((o, i) => (
            <Fragment key={i}>
              {o.type === "group" && !o.hide && (
                <Section borderRadius padding={["lg"]} innerMargin="md">
                  {o.label && (
                    <Header size="sm" weight="bold" color="black">
                      {o.label}
                    </Header>
                  )}

                  <TabList size="lg" direction="column" theme="clean" innerMargin={innerMargin}>
                    <RoutedTabs routeParam={routeParam} views={buildViews(o)} />
                  </TabList>
                </Section>
              )}
            </Fragment>
          ))}
        </Flex>
      )}
    </div>
  );
};
